/**
 * HomeContext — the staff member's personal home data.
 *
 * Client-scoped communications, notifications, and reminders/to-dos that sit on
 * top of the WorkflowContext (tasks, intake, audit). Front-end simulation for
 * now; live wiring swaps the seeds for Gmail/Calendar/Firestore reads.
 */
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

export type Channel = 'email' | 'phone' | 'text' | 'portal';
export type NotifKind = 'alert' | 'warning' | 'info' | 'success';

export interface Comm { id: string; client: string; channel: Channel; subject: string; preview: string; time: string; unread: boolean; }
export interface Notif { id: string; kind: NotifKind; text: string; time: string; read: boolean; }
export interface Reminder { id: string; text: string; client: string; due: string; done: boolean; }

interface HomeState {
  comms: Comm[];
  notifications: Notif[];
  reminders: Reminder[];
  markCommRead: (id: string) => void;
  markAllCommsRead: () => void;
  markNotifRead: (id: string) => void;
  markAllNotifsRead: () => void;
  toggleReminder: (id: string) => void;
  addReminder: (text: string, client: string, due: string) => void;
}

const SEED_COMMS: Comm[] = [
  { id: 'C1', client: 'Acme Nutraceutical Packaging', channel: 'email', subject: 'Re: Sample request SKUs', preview: 'Jane: I think they match our last reorder — can you confirm the three SKU numbers?', time: '25m ago', unread: true },
  { id: 'C2', client: 'Blue Harbor Foods', channel: 'phone', subject: 'Missed call — proof timing', preview: 'Voicemail asking when the updated artwork proof will be ready.', time: '1h ago', unread: true },
  { id: 'C3', client: 'Peak Wellness', channel: 'text', subject: 'Invoice + tracking', preview: 'Checking on invoice status and a tracking number for the last order.', time: '2h ago', unread: false },
  { id: 'C4', client: 'Acme Nutraceutical Packaging', channel: 'portal', subject: 'Quote Q-2026-000789 viewed', preview: 'Client opened the quote in the portal but has not approved yet.', time: 'Yesterday', unread: false },
];

const SEED_NOTIFS: Notif[] = [
  { id: 'N1', kind: 'alert', text: 'TASK-2026-1003 is blocked — vendor confirmation overdue for Acme.', time: '10m ago', read: false },
  { id: 'N2', kind: 'info', text: 'New intake INT-2026-0004 from Peak Wellness needs triage.', time: '40m ago', read: false },
  { id: 'N3', kind: 'warning', text: 'Quote Q-2026-000789 awaiting client approval for 26h.', time: 'Today', read: false },
  { id: 'N4', kind: 'success', text: 'Blueprint BP-SKU-ACME-0007-V03 approved for reorder.', time: '3h ago', read: true },
];

const SEED_REMINDERS: Reminder[] = [
  { id: 'R1', text: 'Email Jane to confirm the three sample SKUs', client: 'Acme Nutraceutical Packaging', due: 'Today 2:00 PM', done: false },
  { id: 'R2', text: 'Call Blue Harbor back about proof timing', client: 'Blue Harbor Foods', due: 'Today 4:00 PM', done: false },
  { id: 'R3', text: 'Send Peak Wellness invoice + tracking number', client: 'Peak Wellness', due: 'Tomorrow 10:00 AM', done: false },
  { id: 'R4', text: 'Follow up on Acme quote approval', client: 'Acme Nutraceutical Packaging', due: 'Fri 9:00 AM', done: false },
];

const Ctx = createContext<HomeState | null>(null);

export function HomeProvider({ children }: { children: ReactNode }) {
  const [comms, setComms] = useState<Comm[]>(SEED_COMMS);
  const [notifications, setNotifs] = useState<Notif[]>(SEED_NOTIFS);
  const [reminders, setReminders] = useState<Reminder[]>(SEED_REMINDERS);

  const markCommRead = useCallback((id: string) => setComms((c) => c.map((x) => (x.id === id ? { ...x, unread: false } : x))), []);
  const markAllCommsRead = useCallback(() => setComms((c) => c.map((x) => ({ ...x, unread: false }))), []);
  const markNotifRead = useCallback((id: string) => setNotifs((n) => n.map((x) => (x.id === id ? { ...x, read: true } : x))), []);
  const markAllNotifsRead = useCallback(() => setNotifs((n) => n.map((x) => ({ ...x, read: true }))), []);
  const toggleReminder = useCallback((id: string) => setReminders((r) => r.map((x) => (x.id === id ? { ...x, done: !x.done } : x))), []);
  const addReminder = useCallback((text: string, client: string, due: string) => {
    setReminders((r) => [{ id: `R${Date.now()}`, text, client, due, done: false }, ...r]);
  }, []);

  const value: HomeState = { comms, notifications, reminders, markCommRead, markAllCommsRead, markNotifRead, markAllNotifsRead, toggleReminder, addReminder };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useHome() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useHome must be used within HomeProvider');
  return c;
}

export const channelLabel: Record<Channel, string> = { email: 'Email', phone: 'Call', text: 'Text', portal: 'Portal' };
